import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import pfp from 'assets/pfp.png';

// Combo boot — the XP boot Kris liked (IE globe, "the INTERNET XP" wordmark, the
// three-block sliding bar) as the main event, with a short cinematic terminal
// lead-in + glitch-decode wordmark from the y2kdotcom-inspired pass. Lime-on-black,
// our lore, CRT scanlines. ~4s, click to skip, once per browser session.
//
// AUDIO: ships NO audio. Emits stage events via optional onStep(name):
//   'power'(mount) · 'post' · 'log'(per term line) · 'ramp'(% counter) ·
//   'decode'(wordmark glitch) · 'ready'(resolve). <Boot onStep={n => sfx.boot(n)} />

const KEY = 'ic:booted3';
const STEP_MS = 230;
const TERM_MS = 1300;   // terminal lead-in
const XP_MS = 2200;     // the XP boot (main event)
const FADE_MS = 460;
const SCRAMBLE = '!<>-_/[]{}=+*#01ABCDEF█▓▒░@$%';

const TERM_LINES = [
  '> run internet_coin.exe',
  '> 56K modem ... handshake ~~~ CONNECTED',
  '> loading nostalgia.core ... %P%%',
];
const RAMP_IDX = 2;

// scramble `text` toward resolved over ~durationMs, then hold static
function Scramble({ text, durationMs }) {
  const [out, setOut] = useState(text);
  useEffect(() => {
    let frame = 0;
    const total = Math.max(8, Math.round(durationMs / 34));
    const id = setInterval(() => {
      frame += 1;
      const revealed = Math.floor((frame / total) * text.length);
      let s = '';
      for (let i = 0; i < text.length; i++) {
        if (i < revealed) s += text[i];
        else s += SCRAMBLE[(Math.random() * SCRAMBLE.length) | 0];
      }
      setOut(s);
      if (frame >= total) { setOut(text); clearInterval(id); }
    }, 34);
    return () => clearInterval(id);
  }, [text, durationMs]);
  return <span>{out}</span>;
}

export default function Boot({ onDone, onStep }) {
  const [gone, setGone] = useState(() => {
    try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  });
  const [idx, setIdx] = useState(0);
  const [pct, setPct] = useState(0);
  const [phase, setPhase] = useState('term'); // term -> xp -> fade
  const done = useRef(false);
  const fired = useRef({});
  const stepRef = useRef(onStep);
  stepRef.current = onStep;

  function emit(name, once) {
    if (once) { if (fired.current[name]) return; fired.current[name] = 1; }
    try { stepRef.current && stepRef.current(name); } catch (e) { /* ignore */ }
  }
  function finish() {
    if (done.current) return;
    done.current = true;
    emit('ready', true);
    try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* ignore */ }
    setGone(true);
    onDone && onDone();
  }

  useEffect(() => {
    if (gone) { onDone && onDone(); return; }
    emit('power', true);
    const postT = setTimeout(() => emit('post', true), STEP_MS);
    const reveal = setInterval(() => setIdx(i => {
      if (i >= TERM_LINES.length) return i;
      emit('log');
      if (i === RAMP_IDX) emit('ramp', true);
      return i + 1;
    }), STEP_MS);
    const ramp = setInterval(() => setPct(p => (p < 100 ? Math.min(100, p + 6) : p)), STEP_MS / 8);
    const toXp = setTimeout(() => { setPhase('xp'); emit('decode', true); }, TERM_MS);
    const toFade = setTimeout(() => { setPhase('fade'); emit('ready', true); }, TERM_MS + XP_MS);
    const end = setTimeout(finish, TERM_MS + XP_MS + FADE_MS);
    return () => { clearTimeout(postT); clearInterval(reveal); clearInterval(ramp); clearTimeout(toXp); clearTimeout(toFade); clearTimeout(end); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (gone) return null;
  return (
    <Screen fading={phase === 'fade'} onClick={finish} onTouchStart={finish}>
      {phase === 'term' && (
        <div className="boot__term">
          {TERM_LINES.slice(0, idx).map((l, i) => (
            <div key={i}>{l.replace('%P%', String(pct))}</div>
          ))}
          <span className="boot__cursor">█</span>
        </div>
      )}
      {phase !== 'term' && (
        <div className="boot__center">
          <img src={pfp} alt="" className="boot__mark" />
          <div className="boot__word">
            <span className="boot__the">the</span>
            <Scramble text="INTERNET" durationMs={700} />
            <span className="boot__xp">XP</span>
          </div>
          <div className="boot__bar">
            <div className="boot__blocks"><i /><i /><i /></div>
          </div>
          <div className="boot__line">starting the internet… quoted in MSFT · pays around the clock</div>
        </div>
      )}
      <div className="boot__foot">
        <span>INTERNET COIN · Professional Edition</span>
        <span>click to skip</span>
      </div>
    </Screen>
  );
}

const slide = keyframes`from { transform: translateX(-60px); } to { transform: translateX(240px); }`;
const pulse = keyframes`0%,100%{filter:drop-shadow(0 0 14px rgba(204,255,0,.6))}50%{filter:drop-shadow(0 0 30px rgba(204,255,0,.95))}`;
const blink = keyframes`0%,49%{opacity:1}50%,100%{opacity:0}`;

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: radial-gradient(120% 90% at 50% 45%, #0a0d05 0%, #000 75%);
  color: #ccff00;
  font-family: Tahoma, 'Trebuchet MS', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${p => (p.fading ? 0 : 1)};
  transition: opacity ${FADE_MS}ms ease;
  overflow: hidden;

  .boot__term {
    font-family: 'Consolas', 'Courier New', monospace;
    font-size: 14px;
    line-height: 2;
    text-shadow: 0 0 8px rgba(204,255,0,.45);
    width: min(560px, 86vw);
  }
  .boot__cursor { animation: ${blink} 1s step-end infinite; }
  .boot__center { display: flex; flex-direction: column; align-items: center; gap: 18px; }
  .boot__mark { width: 96px; height: 96px; animation: ${pulse} 1.6s ease-in-out infinite; }
  .boot__word { font-size: 34px; letter-spacing: 6px; color: #fff; font-weight: bold; text-shadow: 0 0 24px rgba(204,255,0,.35); }
  .boot__the { font-weight: normal; font-size: 18px; letter-spacing: 2px; color: #8fb800; vertical-align: middle; margin-right: 6px; }
  .boot__xp { color: #ccff00; font-size: 22px; vertical-align: super; margin-left: 4px; }
  .boot__bar { width: 240px; height: 16px; border: 2px solid #5a7a00; border-radius: 4px; overflow: hidden; background: #0a0c04; }
  .boot__blocks {
    display: flex; gap: 3px; height: 100%; padding: 2px;
    animation: ${slide} 1.1s linear infinite;
    i { display: block; width: 14px; height: 100%; background: #ccff00; box-shadow: 0 0 8px rgba(204,255,0,.8); border-radius: 2px; }
  }
  .boot__line { font-size: 11px; letter-spacing: 2px; color: #8fb800; text-align: center; padding: 0 20px; }
  .boot__foot {
    position: absolute; bottom: 26px; left: 30px; right: 30px;
    display: flex; justify-content: space-between;
    font-size: 11px; color: #5a7a00; letter-spacing: 1px;
  }
`;
